import {
    DateFormat,
    formatDate,
    getExperiencePurchaseItems,
    isMistPurchase,
    isNonCanceledExperienceItem,
    isPartnerPurchase,
    isTaxFee,
    now,
} from "common";
import dayjs from "dayjs";
import { find, isNumber, sumBy } from "lodash";
import {
    AllowedPrivacy,
    BookingEngineSource,
    CatalogItemDisplaySection,
    Experience,
    ExperiencePurchaseItem,
    Form,
    FormResponse,
    GiftPurchaseItem,
    Guest,
    LineItem,
    LineItemCode,
    LineItemTemplateCode,
    LineItemType,
    MembershipPurchaseItem,
    MerchandisePurchaseItem,
    Partner,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseItemPrivacy,
} from "types";
import {
    AddonLineItem,
    CreatePurchaseItemLineItem,
    CreatePurchaseItemState,
    DemographicLineItem,
    MembershipLineItem,
} from "@/stores/createPurchaseStore";

export const isCreatedByPartner = (purchase: Purchase, item: ExperiencePurchaseItem, partner?: Partner) => {
    if (isPartnerPurchase(purchase)) {
        return true;
    }

    // This is affiliate. we need to check partner.user here since createdBy will have user id instead of affiliate id
    if (item.partner && partner?.user) {
        return partner.user.id === purchase.createdBy.id;
    }

    return false;
};

export const getArrivalTime = (item: ExperiencePurchaseItem) => {
    const { arrivalDateTime } = item;
    return isNumber(item.arrivalTime) ? formatDate(arrivalDateTime, DateFormat.TIME) : null;
};

export const getArrivalDate = (item: ExperiencePurchaseItem) => {
    const { arrivalDateTime } = item;
    return item.arrivalDate ? formatDate(arrivalDateTime, DateFormat.DATE_SHORT) : null;
};

export const getTimeslotIdFromPurchaseItem = (
    item: ExperiencePurchaseItem | CreatePurchaseItemState,
    includeEventId = true,
) => {
    const productId = item.product?.id ?? "";
    const arrivalDate = item?.arrivalDate ?? "";
    const arrivalTime = item?.arrivalTime ?? "";
    const eventId = includeEventId && "event" in item ? item.event?.id ?? "" : "";

    const components = [productId, arrivalDate, arrivalTime, eventId];
    const nonEmptyComponents = components.filter((component) => component !== "");

    return nonEmptyComponents.join("_");
};

export const getItemLineItemsByType = <T extends LineItemType>(
    item: { lineItems: Array<CreatePurchaseItemLineItem | LineItem> },
    type: T,
) => {
    return item.lineItems?.filter((lineItem) => type === lineItem.type) ?? [];
};

export const getAddonLineItems = (item: { lineItems: Array<CreatePurchaseItemLineItem | LineItem> }) => {
    return item.lineItems.filter((lineItem): lineItem is AddonLineItem => [LineItemType.ADDON].includes(lineItem.type));
};

export const getDemographicLineItems = (item: { lineItems: Array<CreatePurchaseItemLineItem | LineItem> }) => {
    return item.lineItems.filter((lineItem): lineItem is DemographicLineItem => {
        return (
            "type" in lineItem &&
            lineItem.type === LineItemType.DEMOGRAPHIC &&
            lineItem.code !== LineItemTemplateCode.PER_OUTING_PRICE
        );
    });
};

export const getAttendeeLineItemsForRoster = (item: { lineItems: Array<CreatePurchaseItemLineItem | LineItem> }) => {
    return item.lineItems.filter((lineItem): lineItem is DemographicLineItem => {
        return (
            "type" in lineItem &&
            lineItem.type === LineItemType.DEMOGRAPHIC_SECONDARY &&
            ((lineItem as LineItem).quantity ?? 0) > 0
        );
    });
};

export const getEquipmentRosterLineItems = (item: { lineItems: Array<CreatePurchaseItemLineItem | LineItem> }) => {
    return item.lineItems.filter((lineItem): lineItem is DemographicLineItem => {
        return (
            "type" in lineItem && lineItem.type === LineItemType.EQUIPMENT && ((lineItem as LineItem).quantity ?? 0) > 0
        );
    });
};

export const getMembershipLineItems = (item: {
    lineItems: Array<CreatePurchaseItemLineItem | MembershipLineItem | LineItem>;
}) => {
    return item.lineItems.filter((lineItem): lineItem is MembershipLineItem => {
        return "type" in lineItem && lineItem.type === LineItemType.MEMBERSHIP;
    });
};

export const getMembershipCodeLineItems = (item: { lineItems: LineItem[] }) => {
    return item.lineItems.filter((lineItem) => {
        return (
            "type" in lineItem &&
            lineItem.type === LineItemType.DISCOUNT &&
            "templateCode" in lineItem &&
            lineItem.templateCode === LineItemCode.MEMBERSHIP_DISCOUNT
        );
    });
};

export const getScheduledExperiencePurchaseItem = (items: PurchaseItem[]): ExperiencePurchaseItem | undefined => {
    return items.find((item) => isExperiencePurchaseItem(item)) as ExperiencePurchaseItem;
};

export const getScheduledExperiencePurchaseItems = (items: PurchaseItem[]): ExperiencePurchaseItem[] => {
    const result: ExperiencePurchaseItem[] = [];

    for (const item of items) {
        if (isExperiencePurchaseItem(item)) {
            result.push(item);
        }
    }

    return result;
};

export const isBookingEngine = (source: string) => {
    // Either booking engine name or mongoid of engine plugin
    const bookingEngineSources = Object.values(BookingEngineSource);
    return bookingEngineSources.includes(source as BookingEngineSource) || /^[\da-fA-F]{24}$/.exec(source);
};

export const isPastBooking = (item: PurchaseItem) => {
    const hasArrivalDateTime = "arrivalDateTime" in item;

    if (hasArrivalDateTime) {
        const arrivalDateTime = dayjs(item.arrivalDateTime);
        return arrivalDateTime?.isBefore(now());
    }

    return false;
};

export const getPurchaseItemGuests = (guests: Guest[] | undefined, form?: Form, formResponse?: FormResponse) => {
    if (!guests || guests.length === 0) {
        return [];
    }

    if (form?.organizerOnly && formResponse) {
        /** We need to add `formResponse` to the primary or first guest */
        let primaryGuestIndex = guests.findIndex((guest) => guest.primary);
        if (primaryGuestIndex === -1) {
            primaryGuestIndex = 0;
        }

        const updatedGuests = [...guests];

        updatedGuests[primaryGuestIndex] = {
            ...guests[primaryGuestIndex],
            formResponse: formResponse,
        };

        return updatedGuests;
    }

    return guests;
};

export const isPriceConfigurationUpdated = (previewPurchase: Purchase | null, item: PurchaseItem) => {
    const previewPurchaseItem = item && find(previewPurchase?.items, { id: item.id });
    return previewPurchaseItem?._priceConfigurationUpdated;
};

export const getShortCode = (item: PurchaseItem) => {
    return "shortCode" in item ? item.shortCode : "N/A";
};

/**
 * @param purchase
 * @param initialItem
 * @returns in case of mist purchase returns experience purchase item with nearest date, otherwise initially passed item
 */
export const getNearestPurchaseItem = (purchase: Purchase, initialItem: PurchaseItem) => {
    if (isMistPurchase(purchase)) {
        const experiencePurchaseItems = purchase.items.filter(
            (item): item is ExperiencePurchaseItem => "arrivalDateTime" in item,
        );
        return experiencePurchaseItems?.reduce((previousItem, currentItem) => {
            return dayjs(previousItem.arrivalDate).isAfter(dayjs(currentItem.arrivalDate)) ? currentItem : previousItem;
        });
    }

    return initialItem;
};

export const getCouponLineItems = (item: { lineItems: LineItem[] }) => {
    return item.lineItems.filter((lineItem) => lineItem.coupon);
};

/**
 * Checks if the passed item is not canceled and returns it,
 * or gets 1st available not canceled experience purchase item for the purchase,
 * otherwise the initially passed item.
 * @param purchase
 * @param initialItem
 * @returns {ExperiencePurchaseItem}
 */
export const getFirstAvailableExperiencePurchaseItem = (
    purchase: Purchase,
    initialItem: ExperiencePurchaseItem,
): ExperiencePurchaseItem => {
    const experiencePurchaseItems = getExperiencePurchaseItems(purchase);

    return isNonCanceledExperienceItem(initialItem)
        ? initialItem
        : experiencePurchaseItems.length > 0
        ? experiencePurchaseItems[0]
        : initialItem;
};

export const isAllowedPrivacy = (privacy?: string): privacy is AllowedPrivacy => {
    return privacy === PurchaseItemPrivacy.PRIVATE || privacy === PurchaseItemPrivacy.PUBLIC;
};

export const isScheduledItem = (item: PurchaseItem | CreatePurchaseItemState): boolean => {
    return item.object === PurchaseItemObject.EXPERIENCE || item.object === PurchaseItemObject.EQUIPMENT;
};

export const isPurchaseItemWithAddon = (item: PurchaseItem, experience: Experience) => {
    return item.lineItems.some(
        (lineItem) =>
            lineItem.type &&
            [LineItemType.ADDON, LineItemType.PICKUP].includes(lineItem.type) &&
            !!lineItem.quantity &&
            lineItem.includeInAmount &&
            experience.catalog.items.some(
                (catalogItem) =>
                    catalogItem.template?.id === lineItem.template?.id &&
                    catalogItem.displaySections?.includes(CatalogItemDisplaySection.ADDONS),
            ),
    );
};

export const isExperiencePurchaseItemType = (
    item: ExperiencePurchaseItem | CreatePurchaseItemState,
): item is ExperiencePurchaseItem => {
    return (
        item.object === PurchaseItemObject.EXPERIENCE ||
        (item.object === PurchaseItemObject.ORDER && "shortCode" in item)
    );
};

export const isExperiencePurchaseItem = (
    item: PurchaseItem | CreatePurchaseItemState,
): item is ExperiencePurchaseItem => {
    return (
        item.object === PurchaseItemObject.EXPERIENCE ||
        item.object === PurchaseItemObject.ORDER ||
        item.object === PurchaseItemObject.EQUIPMENT
    );
};

export const isCodeSupported = (itemObject: PurchaseItemObject) => {
    return [
        PurchaseItemObject.EXPERIENCE,
        PurchaseItemObject.EQUIPMENT,
        PurchaseItemObject.ORDER,
        PurchaseItemObject.MEMBERSHIP,
        PurchaseItemObject.WARE,
    ].includes(itemObject);
};

export const getTaxFeeLineItems = (item: PurchaseItem | CreatePurchaseItemState) => {
    return (item.lineItems as LineItem[])?.filter((lineItem) => "type" in lineItem && isTaxFee(lineItem));
};

export const isGiftPurchaseItem = (item?: PurchaseItem | CreatePurchaseItemState): item is GiftPurchaseItem => {
    return item?.object === PurchaseItemObject.GIFT;
};

export const isMembershipItem = (
    item?: PurchaseItem | CreatePurchaseItemState | null,
): item is MembershipPurchaseItem => {
    return item?.object === PurchaseItemObject.MEMBERSHIP;
};

export const isMerchandiseItem = (
    item?: PurchaseItem | CreatePurchaseItemState | null,
): item is MerchandisePurchaseItem => {
    return item?.object === PurchaseItemObject.WARE;
};

export const sumLineItemQuantitiesByType = (lineItems: CreatePurchaseItemLineItem[], type: LineItemType) => {
    return sumBy(
        lineItems.filter((lineItem) => "type" in lineItem && lineItem.type === type),
        "quantity",
    );
};

export const getMerchandiseVariantName = (item: MerchandisePurchaseItem) => {
    const wareLineItems = item.lineItems.filter((lineItem) => lineItem.type === LineItemType.WARE);
    if (wareLineItems.length === 1) {
        return wareLineItems[0].name;
    }
    return null;
};
